import React, { useState, useMemo} from "react";
import { useDispatch } from "react-redux";
import { 
    DOCUMENT_CONTENT_TYPE_PDF
} from "../../../../../app/constants";
import { documentTypeString, humanReadableDate } from "../../../../../app/utils";
import { Button } from "../../../../../components/RegularComponents";
import { CommonAttachmentFileUpload } from "./CommonAttachmentFileUpload";
import { DocumentViewerFileNameLink, DownloadFileNameLink } from "../../../../../components/DocumentViewer/DocumentFileComponent";
import DocumentViewer from "../../../../../components/DocumentViewer/DocumentViewer";
import { fetchDocumentData } from "../../../../../redux/slice/supportingDocumentsSlice"
import { convertFileDataForUrl } from "../../../../../app/documentUploadUtils";
import InputSelectForm from "../../../../../components/InputSelectForm";

export const CommonAttachmentTable = (props) => {

    const { 
        attachments, 
        setAttachment,
        peopleList,
        documentTypes,
        isArchiveApplication
    } = props

    const [sortingParameters, setSortingParameters] = useState({})
    const dispatch = useDispatch();

    const [openDocumentViewer, setOpenDocumentViewer] = useState(false)
    const [selectedFile, setSelectedFile] = useState(null)
    const [documentViewerDocument, setDocumentViewerDocument] = useState(null)

    const [selectedEntityIdMapping, setSelectedEntityIdMapping] = useState({})

    const headers = useMemo(() => {
        const documentComponent = (d) => { 
            return (
                <>
                    { d.documentContentType === DOCUMENT_CONTENT_TYPE_PDF ? 
                        <DocumentViewerFileNameLink
                            id={"bladmin-information-attachment-file-" + d.filename}
                            key={"bladmin-information-attachment-file-" + d.filename}
                            file={d} 
                            onClick={() => {
                                if(d.data) { 
                                    let fileDataUrl = convertFileDataForUrl(d.data, d.documentContentType)
                                    setDocumentViewerDocument(fileDataUrl)
                                    setOpenDocumentViewer(true)
                                    setSelectedFile(d)
                                } else { 
                                    // download data and then view it.
                                    if(d.guid) { 
                                        dispatch(fetchDocumentData(d.guid))
                                            .then(downloadFile => {
                                                if(downloadFile) {
                                                    let fileDataUrl = convertFileDataForUrl(downloadFile.data, downloadFile.documentContentType)
                                                    setDocumentViewerDocument(fileDataUrl)
                                                    setOpenDocumentViewer(true)
                                                    setSelectedFile(downloadFile)
                                                }
                                            })
                                            .catch((error) => {
                                                console.error(error)
                                            })
                                    }
                                }
                            }}
                        /> : 
                        <DownloadFileNameLink
                            id={"bladmin-information-attachment-download-file-" + d.filename}
                            componentKey={"bladmin-information-attachment-download-file-" + d.filename}
                            file={d} 
                        />}
                </>
            )
        }

        const peopleListSelectComponent = (rowIndex) => {
            if(peopleList) {
                return (
                    <InputSelectForm
                        id={"upload-criminal-record-check-select"}
                        peopleList
                        options={peopleList}
                        currentValue={selectedEntityIdMapping[rowIndex]}
                        onChange={(e) => {
                            let updatedObj = {...selectedEntityIdMapping}
                            Object.defineProperty(updatedObj, rowIndex, {value : e.target.value, enumerable: true, writable: true})
                            setSelectedEntityIdMapping(updatedObj)
                        }}
                    />)
            }
    
            return null
        }

        let headers = [{
            key: "filename",
            title: "File name",
            sort: (isAscending) => (a, b) => {
                const aStr = a.filename
                const bStr = b.filename
                return isAscending ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
            },
            format: file => (<span style={{cursor: "pointer"}}>{documentComponent(file)}</span>)
        }]

        if(documentTypes && Array.isArray(documentTypes) && documentTypes.length > 1) {
            headers.push({
                key: "documentType",
                title: "File type",
                sort: (isAscending) => (a, b) => {
                    const aStr = a
                    const bStr = b
                    return isAscending ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
                },
                format: ({documentType}) => documentTypeString(documentType)
            })
        }

        if(props.showEntityDetails) {
            const entityHeaders = [{
                key: "entityName",
                title: "Name",
                sort: (isAscending) => (a, b) => {
                    const aStr = a.entityName
                    const bStr = b.entityName
                    return isAscending ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
                },
                format: ({entityName}, rowIndex) => (entityName ? <span>{entityName.split(",").map(s => (<>{s.trim()}<br /></>))}</span> : peopleListSelectComponent(rowIndex))
            }]

            headers = [...headers, ...entityHeaders]
        }

        return [
            ...headers,
            {
                key: "createdDate",
                title: "Date received",
                sort: (isAscending) => (a, b) => {
                    return isAscending ? a.createdDate - b.createdDate : b.createdDate - a.createdDate
                },
                format: ({createdDate}) => humanReadableDate(createdDate)
            },
            {
                key: "createdBy",
                title: "Uploaded by",
                sort: (isAscending) => (a, b) => {
                    const aStr = `${a.createdBy}`
                    const bStr = `${b.createdBy}`
                    return isAscending ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
                }
            }
        ]
    }, [
        setDocumentViewerDocument,
        setOpenDocumentViewer,
        setSelectedFile,
        dispatch,
        documentTypes,
        props.showEntityDetails,
        peopleList,
        selectedEntityIdMapping
    ])
    
    const rows = useMemo(() => {
        if (!attachments) { return [] }

        const header = headers.find(h => h.key === sortingParameters.key)
        if (!header) { return attachments }

        const newBranches = [...attachments]
        newBranches.sort(header.sort(sortingParameters.isAscending));
        return newBranches

    }, [sortingParameters, attachments, headers])

    const getSortIcon = (header) => {
        if (header.key !== sortingParameters?.key) {
            return <i style={{ color: "lightgray" }} className={`fa fa-lg fa-sort`} />
        }
        const activeClass = header.key === sortingParameters?.key ? "active" : ""
        const activeStyle = header.key === sortingParameters?.key ? "#0058a4" : "lightgray"
        const iconStr = sortingParameters?.isAscending ? "fa-sort-asc" : "fa-sort-desc"
        return <i style={{ color: activeStyle }} className={`fa fa-lg ${activeClass} ${iconStr}`} />
    }

    const headerClicked = (header) => () => {
        let modifiedSortingParams = { ...sortingParameters }

        if (header.key === modifiedSortingParams.key) {
            modifiedSortingParams.isAscending = !modifiedSortingParams.isAscending
        } else {
            modifiedSortingParams = { key: header.key, isAscending: false }
        }

        setSortingParameters(modifiedSortingParams)
    }

    return (
        <div className="licence-application-details-panel">
            {!isArchiveApplication && 
            <CommonAttachmentFileUpload 
                documentTypes={documentTypes}
                handleFileUpload = {props.onFileUpload}
            />}

            <div className="row section-row-margin">
                <table className="attachment_table" style={{fontSize: "0.9em"}}>
                    <thead>
                        <tr>
                            {headers.map((h) => (
                                <th key={h.key} onClick={headerClicked(h)}>
                                    <div style={{
                                        display: "inline-flex",
                                        width: "100%",
                                        gap: "0.5rem",
                                        alignItems: "center",
                                        cursor: "arrow"
                                    }}><span>{h.title}</span> <span style={{ marginRight: "6px" }}>{getSortIcon(h)}</span></div>
                                </th>
                            ))}
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={index}>
                                {headers.map((header, headerIndex) => {
                                    const key = header.key;
                                    const value = row[key]
                                    const formattedValue = header.format ? header.format(row, index) : value;
                                    return <td key={headerIndex}>{formattedValue}</td>
                                })}
                                <td>
                                    { (props.showEntityDetails 
                                        && !row.entityId) ?
                                        <Button id={`attachment-detail-${index}`}
                                            className={"licence-queue-review-readonly-button"}
                                            buttonType="regular_success"
                                            text="Save"
                                            onClick={() => {
                                                let fileObj = {
                                                    ...row, 
                                                    entityId : selectedEntityIdMapping[index]
                                                }
                                                props.onFileUpload(fileObj)
                                            }} />

                                        : <Button id={`attachment-detail-${index}`}
                                            className={"licence-queue-review-readonly-button"}
                                            buttonType="regular_primary"
                                            text="Details"
                                            onClick={() => {
                                                    setAttachment(row)
                                            }} />

                                    }
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {/* <div className="collector-licence-col col-md-4" style={{ flexGrow: 1, display: "flex", fontWeight: "bold" }}>
                    <span>Client documents</span>
                </div>

                <div className="collector-licence-col col-md-6" style={{ display: "flex", flexDirection: "column" }}>
                    {documents.map((document, index) => (
                        <div key={index}>
                            <DownloadFileNameLink
                                id={"supporting-document-download-file-" + document.filename}
                                // key={"supporting-document-download-file-" + document.filename}
                                file={document}
                            />
                        </div>
                    ))}
                </div> */}
            </div>

            <DocumentViewer 
                fileData={documentViewerDocument}
                isOpen={openDocumentViewer}
                fileName={selectedFile?.filename}
                handleClose={() => setOpenDocumentViewer(false)}
            />
        </div>
    )
}